// src/lib/festival-criteria.js: niveau-onderscheidende criteria per
// functiefamilie (Functiehandboek Filmfestivals 2.0, dec 2025).
//
// Twee soorten criteria:
//  - "kwantitatief"  → Hoofd- en Directie-families: 6-8 criteria, gebruiker
//                       scoort niveau 1..max (3 voor Hoofden, 4 voor Directie).
//                       Score-logica: cumulatief van zwaarste naar lichtste niveau,
//                       eerste niveau dat ≥ `norm.vereist` cumulatieve scores haalt
//                       bepaalt de variant via `variantBijNiveau`. Directie-families
//                       hebben een `fallbackVariant` (laagste schaal) wanneer de
//                       norm niet gehaald wordt, handboek toont dit als lege A-rij.
//  - "kwalitatief"   → Medewerker / Coördinator / Programmeur: 4-5 resultaat-
//                       gebieden, gebruiker kiest A of B per gebied. Variant = letter
//                       waarop ≥ `norm.vereist` gescoord is (B prevaleert).
//
// Bron: bijlage "Uitleg Toepassing Criteria/Niveaus Functies Directeur/Hoofden"
// (p. 55-56). Voorbeeld uit handboek: 2× niveau 2 + 2× niveau 3 → variant op
// niveau 3; 2× niveau 1 + 2× niveau 2 → variant op niveau 2.

export const FAMILIE_CRITERIA = {
  // ============================================================
  // KWANTITATIEF: HOOFD-families (A/B/C, schaal 7-9)
  // ============================================================
  "Hoofd Productie": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 7 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 9–10.
    // Teksten, definities en volgorde 1-op-1 overgenomen.
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen t.a.v. het resultaat geven aan één of twee type werkenden. Bij toepassing meerdere criteria prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 16" },
              { niveau: 2, label: "6 – 15" },
              { niveau: 3, label: "1 – 5" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan zzp-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 25" },
              { niveau: 2, label: "16 – 24" },
              { niveau: 3, label: "1 – 15" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        toelichting: "Het aantal locaties en/of zalen waarin het festivalprogramma plaatsvindt voor publiek en/of professionals.",
        keuzes: [
          { niveau: 1, label: "x ≥ 16" },
          { niveau: 2, label: "6 – 15" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet / jaarrekening en/of beheer afdelingsbudget",
        toelichting: "De binnen de strategische keuzes gerealiseerde omzet (inclusief personele lasten) zoals gerapporteerd in de jaarrekening en/of beheer afdelingsbudget (exclusief personele lasten). Bij keuze meerdere criteria prevaleert de totaalscore op het beheer afdelingsbudget.",
        subCriteria: [
          {
            id: "jaaromzet",
            label: "Jaaromzet festival",
            keuzes: [
              { niveau: 1, label: "Groot met een jaaromzet van € 5.000.001 ≤" },
              { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
              { niveau: 3, label: "Klein met een jaaromzet t/m € 1.000.000" },
            ],
          },
          {
            id: "afdelingsbudget",
            label: "Afdelingsbudget (prevaleert)",
            keuzes: [
              { niveau: 1, label: "Groot met een afdelingsbudget van € 1.000.001 ≤" },
              { niveau: 2, label: "Middelgroot met een afdelingsbudget van € 100.001 t/m € 1.000.000" },
              { niveau: 3, label: "Klein met een jaarbudget t/m € 100.000" },
            ],
          },
        ],
        aggregatie: { strategie: "prevaleert", winnaar: "afdelingsbudget" },
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Het totaal aantal geregistreerde bezoeken aan films en festivalactiviteiten zoals gerapporteerd in het jaarverslag.",
        keuzes: [
          { niveau: 1, label: "x ≥ 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "x ≤ 50.000" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Omstandigheid waarbinnen moet worden gecommuniceerd en de aard daarbij van de communicatie.",
        keuzes: [
          { niveau: 1, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van project- of filmfestival brede besluiten en het hier over communiceren." },
          { niveau: 2, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van doorslaggevende afdelings-overstijgende besluiten en hier over communiceren." },
          { niveau: 3, label: "Bij de operationele uitvoering van projecten / filmfestivals het informeren en beïnvloeden van andere afdelingshoofden en de omgeving." },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        toelichting: "Bijdragen aan het beleid/de resultaten van de gehele organisatie.",
        keuzes: [
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes en besluitvorming / lid MT." },
          { niveau: 2, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling en het leveren van functionele bijdragen aan de integrale beleidsontwikkeling." },
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling." },
        ],
      },
    ],
  },

  "Hoofd Programma": {
    type: "kwantitatief",
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 17–18.
    // Norm én criteria-set verschillen tussen de twee sub-families; teksten,
    // definities en volgorde zijn letterlijk overgenomen uit het handboek.
    subFamilies: [
      {
        id: "programma",
        label: "Hoofd Programma",
        norm: { vereist: 5, totaal: 8 },
      },
      {
        id: "educatie-talent-industry-nieuwe-media",
        label: "Hoofd Educatie en/of Talent en/of Industry en/of Nieuwe Media",
        norm: { vereist: 4, totaal: 7 },
        uitgesloten: ["bezoeken"],
      },
    ],
    defaultSubFamilieId: "programma",
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen t.a.v. het resultaat geven aan één of twee type werkenden. Bij toepassing meerdere criteria prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct en indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 16" },
              { niveau: 2, label: "6 – 15" },
              { niveau: 3, label: "1 – 5" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan externe programmeurs, viewers, selectiecommissies, zzp-ers (alléén direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 41" },
              { niveau: 2, label: "21 – 40" },
              { niveau: 3, label: "1 – 20" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        toelichting: "Het aantal locaties en/of zalen waarin het festivalprogramma plaatsvindt voor publiek en/of professionals.",
        keuzes: [
          { niveau: 1, label: "x ≥ 16" },
          { niveau: 2, label: "6 – 15" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet / jaarrekening en/of beheer afdelingsbudget",
        toelichting: "De binnen de strategische keuzes gerealiseerde omzet zoals gerapporteerd in de jaarrekening (inclusief personele lasten) en/of beheer afdelingsbudget (exclusief personele lasten). Bij keuze meerdere criteria prevaleert de totaalscore op het beheer afdelingsbudget.",
        subCriteria: [
          {
            id: "jaaromzet",
            label: "Jaaromzet festival",
            keuzes: [
              { niveau: 1, label: "Groot met een jaaromzet van € 5.000.001 ≤ x" },
              { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
              { niveau: 3, label: "Klein met een jaaromzet t/m € 1.000.000" },
            ],
          },
          {
            id: "afdelingsbudget",
            label: "Afdelingsbudget (prevaleert)",
            keuzes: [
              { niveau: 1, label: "Groot met een afdelingsbudget van € 1.000.001 ≤ x" },
              { niveau: 2, label: "Middelgroot met een afdelingsbudget van € 100.001 t/m € 1.000.000" },
              { niveau: 3, label: "Klein met een afdelingsbudget t/m € 100.000" },
            ],
          },
        ],
        aggregatie: { strategie: "prevaleert", winnaar: "afdelingsbudget" },
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Het totaal aantal geregistreerde bezoeken aan films en festivalactiviteiten zoals gerapporteerd in het jaarverslag. Alleen van toepassing op Hoofd Programma.",
        keuzes: [
          { niveau: 1, label: "x ≥ 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "x ≤ 50.000" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Omstandigheid waarbinnen moet worden gecommuniceerd en de aard daarbij van de communicatie. Bij toepassing meerdere criteria prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern",
            keuzes: [
              { niveau: 1, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van project- of filmfestival brede besluiten en het hier over communiceren." },
              { niveau: 2, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van doorslaggevende afdelings-overstijgende besluiten en hier over communiceren." },
              { niveau: 3, label: "Bij de operationele uitvoering van projecten / filmfestivals het informeren en beïnvloeden van de omgeving." },
            ],
          },
          {
            id: "extern",
            label: "Extern",
            keuzes: [
              { niveau: 1, label: "Het inhoudelijk en zakelijk afstemmen van de selectie, bekostiging en randprogrammering met externe partijen binnen het festivalbeleid en jaarplan." },
              { niveau: 2, label: "Het inhoudelijk afstemmen van de selectie, bekostiging en randprogrammering met externe partijen. Binnen de door de directie aangegeven kaders." },
              { niveau: 3, label: "Het samenstellen en aanbieden van programma's op maat voor externe partners op basis van periodiek overleg met en concrete aanwijzingen door de artistiek directeur." },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        toelichting: "Bijdragen aan het beleid/de resultaten van de gehele organisatie.",
        keuzes: [
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes en besluitvorming / lid MT." },
          { niveau: 2, label: "Betrokken bij beleidsontwikkeling eigen team/afdeling en leveren functionele bijdragen aan de integrale beleidsontwikkeling." },
          { niveau: 3, label: "Betrokken bij beleidsontwikkeling eigen team/afdeling." },
        ],
      },
      {
        id: "functiebreedte",
        label: "Functiebreedte",
        toelichting: "Leidinggeven aan aantal deelsectoren.",
        keuzes: [
          { niveau: 1, label: "Vier deelsectoren: Programma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 2, label: "Drie deelsectoren: Programma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 3, label: "Minder dan drie deelsectoren." },
        ],
      },
    ],
  },

  "Hoofd Development": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 6 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 26.
    // 6 criteria: geen "aantal bezoeken" of "functiebreedte"; omzet is
    // single (geen afdelingsbudget-keuze, alleen jaaromzet).
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen t.a.v. het resultaat geven aan één en/of twee typen werkenden. Bij toepassing meerdere criteria prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 7" },
              { niveau: 2, label: "4 – 6" },
              { niveau: 3, label: "1 – 3" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan zzp-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 15" },
              { niveau: 2, label: "7 – 14" },
              { niveau: 3, label: "1 – 6" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival worden verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        toelichting: "Het aantal locaties en/of zalen waarin het festivalprogramma plaatsvindt voor publiek en/of professionals.",
        keuzes: [
          { niveau: 1, label: "x ≥ 16" },
          { niveau: 2, label: "6 – 15" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet / jaarrekening",
        toelichting: "De binnen de strategische doelstellingen gerealiseerde omzet zoals gerapporteerd in de jaarrekening (inclusief personele lasten).",
        keuzes: [
          { niveau: 1, label: "Groot met een jaaromzet van x ≥ € 5.000.001" },
          { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
          { niveau: 3, label: "Klein met een jaaromzet t/m € 1.000.000" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Omstandigheid waarbinnen moet worden gecommuniceerd en de aard daarbij van de communicatie.",
        keuzes: [
          { niveau: 1, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van project- of filmfestival brede besluiten en het hier over communiceren." },
          { niveau: 2, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van doorslaggevende afdelings-overstijgende besluiten en hier over communiceren." },
          { niveau: 3, label: "Bij de operationele uitvoering van projecten / filmfestivals het informeren en beïnvloeden van de omgeving." },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        toelichting: "Bijdragen aan het beleid/de resultaten van de gehele organisatie.",
        keuzes: [
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes en besluitvorming / lid MT." },
          { niveau: 2, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling en levert functionele bijdragen aan de integrale beleidsontwikkeling." },
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling." },
        ],
      },
    ],
  },

  "Hoofd Marketing & Communicatie": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 6 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 33–34.
    // 6 criteria: eigen drempels voor afdelingsbudget (€ 500.001 grens,
    // exclusief barters in communicatiebudget).
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen t.a.v. het resultaat geven aan één en/of twee typen werkenden. Bij toepassing meerdere keuzes prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 9" },
              { niveau: 2, label: "4 – 8" },
              { niveau: 3, label: "1 – 3" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan ZZP-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 16" },
              { niveau: 2, label: "6 – 15" },
              { niveau: 3, label: "1 – 5" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        toelichting: "Het aantal locaties en/of zalen waarin het festivalprogramma plaatsvindt voor publiek en/of professionals.",
        keuzes: [
          { niveau: 1, label: "x ≥ 16" },
          { niveau: 2, label: "6 – 15" },
          { niveau: 3, label: "1 – 5" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet / jaarrekening en/of beheer afdelingsbudget",
        toelichting: "De gerealiseerde omzet zoals gerapporteerd in de jaarrekening (inclusief personele lasten) en/of beheer afdelingsbudget (exclusief personele lasten en exclusief barters in het communicatiebudget). Bij keuze meerdere criteria prevaleert de totaalscore op het beheer afdelingsbudget.",
        subCriteria: [
          {
            id: "jaaromzet",
            label: "Jaaromzet festival",
            keuzes: [
              { niveau: 1, label: "Groot met een jaaromzet van x ≥ € 5.000.001" },
              { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
              { niveau: 3, label: "Klein met een jaaromzet t/m € 1.000.000" },
            ],
          },
          {
            id: "afdelingsbudget",
            label: "Afdelingsbudget (prevaleert)",
            keuzes: [
              { niveau: 1, label: "Groot met een afdelingsbudget van x ≥ € 500.001" },
              { niveau: 2, label: "Middelgroot met een afdelingsbudget van € 100.001 t/m € 500.000" },
              { niveau: 3, label: "Klein met een afdelingsbudget t/m € 100.000" },
            ],
          },
        ],
        aggregatie: { strategie: "prevaleert", winnaar: "afdelingsbudget" },
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Omstandigheid waarbinnen moet worden gecommuniceerd en de aard daarbij van de communicatie.",
        keuzes: [
          { niveau: 1, label: "Bij de operationele uitvoering van projecten / filmfestivals het nemen van project- of filmfestival brede besluiten en het hier over communiceren." },
          { niveau: 2, label: "Bij de operationele uitvoering van projecten / festivals het nemen van doorslaggevende afdelings-overstijgende besluiten en hier over communiceren." },
          { niveau: 3, label: "Bij de operationele uitvoering van projecten / festivals het informeren en beïnvloeden van de omgeving." },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        toelichting: "Bijdragen aan het beleid/de resultaten van de gehele organisatie.",
        keuzes: [
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes en besluitvorming / lid MT." },
          { niveau: 2, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling en levert functionele bijdragen aan de integrale beleidsontwikkeling." },
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling." },
        ],
      },
    ],
  },

  // ============================================================
  // KWALITATIEF: MEDEWERKER- en COÖRDINATOR-families (A/B, schaal 5-7)
  // ============================================================
  "Medewerker Productie": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Vertaalt productiedoelstellingen naar operationele werkzaamheden van een aantal bekende en reguliere projecten/activiteiten." },
          { variant: "B", label: "Vertaalt productiedoelstellingen naar operationele werkzaamheden van bekende en nieuwe projecten/activiteiten." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 werk- en denkniveau en ervaring met meer samengestelde producties" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: de door de medewerker productie te behandelen vraagstukken en op te lossen problemen beperken zich tot vaak bekende vraagstukken/problemen binnen de afdeling productie. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en voldoend aan bestaande protocollen." },
          { variant: "B", label: "Moeilijk: de door de medewerker productie te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling productie. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties, het formuleren van alternatieve, out-of-the-box oplossingen en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en voldoend aan bestaande protocollen." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling om af te stemmen over de voortgang van werkzaamheden. Extern: Onderhoudt extern contact met diverse partijen om af te stemmen over de werkzaamheden." },
          { variant: "B", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling en/of andere afdelingen om af te stemmen over de voortgang van werkzaamheden. Heeft contact met betrokken medewerkers en/of freelancers over de inhoud en werkwijze van de productie o.a. om te evalueren. Extern: Onderhoudt extern contact met diverse partijen om af te stemmen over de werkzaamheden." },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geeft functioneel leiding aan vrijwilligers." },
          { variant: "B", label: "Geeft aanwijzingen ten aanzien van het resultaat (functioneel) aan 1 tot en met 3 freelancers. Geeft functioneel leiding aan vrijwilligers. Beoordeelt de prestaties van de vrijwilligers en stemt daarover af met de leidinggevende, instrueert, coacht en motiveert." },
        ],
      },
    ],
  },

  "Medewerker Programma": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Vertaalt programmadoelstellingen naar operationele werkzaamheden van één of een beperkt aantal projecten/activiteiten." },
          { variant: "B", label: "Vertaalt programmadoelstellingen naar operationele werkzaamheden van meerdere projecten/activiteiten." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 werk- en denkniveau en ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: de door de medewerker programma te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling programma. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en het voldoen aan bestaande protocollen." },
          { variant: "B", label: "Moeilijk: de door de medewerker programma te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling programma. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties, het formuleren van alternatieve, out-of-the-box oplossingen en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en het voldoen aan bestaande protocollen." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling om af te stemmen over de voortgang van werkzaamheden. Heeft contacten met vrijwilligers. Extern: Onderhoudt extern contact met diverse partijen om af te stemmen over de werkzaamheden." },
          { variant: "B", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling en/of andere afdelingen om af te stemmen over de voortgang van werkzaamheden. Heeft contact met enkele freelancers over de inhoud en werkwijze van de programma werkzaamheden om te evalueren. Heeft contacten met vrijwilligers. Extern: Onderhoudt extern contact met diverse partijen om af te stemmen over de werkzaamheden." },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geen aanvullende leidinggevende taak." },
          { variant: "B", label: "Geeft aanwijzingen aan 1 tot en met 3 zzp-ers/freelancers. Beoordeelt het resultaat en stemt daarover af met de leidinggevende." },
        ],
      },
    ],
  },

  "Medewerker Development": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Vertaalt development doelstellingen naar operationele werkzaamheden van een aantal projecten/activiteiten." },
          { variant: "B", label: "Vertaalt development doelstellingen van samengestelde en ingewikkelde programma's naar operationele werkzaamheden van een aantal projecten/activiteiten." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 werk- en denkniveau met ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: de door de medewerker development te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling development. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en voldoet aan bestaande protocollen." },
          { variant: "B", label: "Moeilijk tot erg moeilijk: de door de medewerker development te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling development. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties, het formuleren van alternatieve, out-of-the-box oplossingen en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en voldoet aan bestaande protocollen." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling om af te stemmen over de voortgang van werkzaamheden. Extern: Onderhoudt extern contact met enkele, bekende partijen om af te stemmen over de werkzaamheden." },
          { variant: "B", label: "Intern: Onderhoudt intern contact met medewerkers van de eigen afdeling en/of andere afdelingen om af te stemmen over de voortgang van werkzaamheden. Heeft contact met enkele medewerkers of freelancers over de inhoud en werkwijze van de development werkzaamheden om te evalueren. Extern: Onderhoudt extern contact met diverse partijen om af te stemmen over de werkzaamheden." },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geen aanvullende leidinggevende taak." },
          { variant: "B", label: "Geeft aanwijzingen t.a.v. het resultaat aan 1 tot en met 3 zzp-ers/freelancers en/of medewerkers. Beoordeelt de prestaties, instrueert, coacht en motiveert medewerkers c.q. resultaten van zzp-ers/freelancers. Geeft functioneel leiding aan vrijwilligers." },
        ],
      },
    ],
  },

  "Medewerker Marketing & Communicatie": {
    type: "kwalitatief",
    norm: { vereist: 3, totaal: 4 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Leveren van input bij de ontwikkeling van het marketing en communicatie beleid/jaarplan en draagt zorg voor de uitvoering." },
          { variant: "B", label: "Het coördineren en deels uitvoeren van de werkprocessen rondom online marketing (kanalen (SEO, SEA, sociale media, e-mailmarketing, etc.). Het vastleggen van processen in een planning, het monitoren en controleren daarvan en sturen op output, het werkgericht aansturen van webredacteuren, de applicatiebeheerder en externe partijen." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau met minimaal 2 jaar relevante ervaring" },
          { variant: "B", label: "MBO 4 / HBO werk- en denkniveau met minimaal 2 jaar relevante ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: de door de medewerker marketing & communicatie A te behandelen vraagstukken en op te lossen problemen beperken zich tot de afdeling marketing & communicatie. Probleemoplossing bestaat uit een afweging van voor- en nadelen van verschillende opties, het formuleren van alternatieve, out-of-the-box oplossingen en het kiezen van de beste oplossing, onder begeleiding van een leidinggevende en voldoend aan bestaande procedures." },
          { variant: "B", label: "Moeilijk tot erg moeilijk: de door de medewerker marketing & communicatie B te behandelen vraagstukken en op te lossen problemen behelsen ook het afwegen en overzien van consequenties voor andere afdelingen." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Met medewerkers van de eigen afdeling om af te stemmen over de voortgang van werkzaamheden. Met medewerkers buiten het team over werkprocessen om zich te informeren en af te stemmen. Extern: Met leveranciers over geleverde producten en diensten om af te stemmen." },
          { variant: "B", label: "Intern: Met medewerkers van de eigen afdeling en/of van andere afdelingen over de voortgang van de werkzaamheden om af te stemmen, te adviseren en te overtuigen. Met vrijwilligers om te informeren. Extern: Met externe partijen over geleverde producten en diensten om af te stemmen en te overtuigen." },
        ],
      },
    ],
  },

  "Coördinator Programma": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Coördineert de uitvoering van een subonderdeel van het afdelingsbeleid volgens het jaarplan en voert dit deels uit." },
          { variant: "B", label: "Levert input bij de ontwikkeling en monitoring van een sub-onderdeel van het afdelingsbeleid volgens het jaarplan, coördineert de uitvoering ervan en voert dit deels uit. Stelt de jaarplanning op in samenwerking met de eigen leidinggevende, coördineert met andere afdelingen, en houdt onder verantwoordelijkheid van de eigen leidinggevende toezicht op de uitvoering." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO4 - /HBO werk- en denkniveau" },
          { variant: "B", label: "HBO werk- en denkniveau" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: de door de programmacoördinator te behandelen vraagstukken en op te lossen problemen behelsen met name het afwegen en overzien van consequenties voor de eigen afdeling." },
          { variant: "B", label: "Erg moeilijk: de door de programmacoördinator te behandelen vraagstukken en op te lossen problemen behelsen ook het afwegen en overzien van consequenties voor andere afdelingen." },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Heeft (onder verantwoordelijkheid van de eigen leidinggevende) een beperkte leidinggevende taak." },
          { variant: "B", label: "Heeft (onder verantwoordelijkheid van de eigen leidinggevende) een beperkte leidinggevende taak." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Met medewerkers van de eigen afdeling en andere afdelingen om af te stemmen over de voortgang van werkzaamheden. Met medewerkers over de inhoud en werkwijze van het programma om te evalueren. Heeft contact met zzp-ers/freelancers ten behoeve van voortgang en evaluatie resultaten. Extern: Met een beperkt aantal partijen om af te stemmen over de te verrichten en verrichte werkzaamheden." },
          { variant: "B", label: "Intern: Met medewerkers van de eigen afdeling om af te stemmen en te besluiten en met andere afdelingen om af te stemmen over de voortgang van werkzaamheden. Met medewerkers over de inhoud en werkwijze van het programma om te evalueren. Heeft contact met zzp-ers/freelancers ten behoeve van voortgang en evaluatie resultaten. Extern: Met diverse partijen om af te stemmen over de te verrichten en verrichte werkzaamheden." },
        ],
      },
    ],
  },

  "Programmeur": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Stelt programma's samen binnen de door de artistiek directeur opgestelde missie, visie en strategie. Assisteert bij de uitvoering van het programmabeleid en draagt bij aan een deel van de selectiecriteria, onder verantwoordelijkheid van een artistiek directeur of hoofd." },
          { variant: "B", label: "Levert vanuit het eigen aandachtsgebied inhoudelijk input voor de organisatiebrede (artistieke) missie, visie en strategie en vertaalt deze naar een artistiek filmbeleid, legt deze voor ter goedkeuring aan hoofd en/of directie. Draagt zorg voor het ontwikkelen en implementeren van selectiecriteria en programmabeleid." },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "HBO werk- en denkniveau" },
          { variant: "B", label: "HBO+/WO werk- en denkniveau" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: Lost uitvoerende vraagstukken op, geen strategische verantwoordelijkheid. Adviseert bij beslissingen, maar heeft geen eindverantwoordelijkheid." },
          { variant: "B", label: "Erg moeilijk: Beslist over complexe vraagstukken die consequenties hebben voor meerdere afdelingen. Heeft besluitvormingsbevoegdheid over de programmering." },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geeft aanwijzingen ten aanzien van het resultaat (functioneel) aan freelancers en externe viewers, selectiecommissies. Kan stagiairs begeleiden." },
          { variant: "B", label: "Geeft aanwijzingen ten aanzien van het resultaat (functioneel) aan freelancers, externe viewers, selectiecommissies. Kan stagiairs begeleiden. Fungeert als mentor voor Programmeur A." },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: Ondersteunend en adviserend. Gericht op uitwisseling van communicatie. Extern: Programma uitwerking van kleinere samenwerkingen." },
          { variant: "B", label: "Intern: Sturende, adviserende en overtuigende rol. Extern: Onderhandelt over inhoudelijke samenwerkingen met stakeholders en partners." },
        ],
      },
    ],
  },

  // ============================================================
  // KWANTITATIEF: DIRECTIE-families (A/B/C/D/E, schaal 8-12)
  // ============================================================
  // Score-logica: niveau 1 (zwaarste) → E (12), 2 → D (11), 3 → C (10),
  // 4 → B (9). Norm niet gehaald → fallback A (8): de lege rij in het
  // handboek. Criteria-data uit p. 49 (Algemeen), p. 51 (Zakelijk),
  // p. 53 (Artistiek).

  "Algemeen Directeur": {
    type: "kwantitatief",
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 49–50.
    // De directeur-bestuurder (RvT-model) scoort volledig; de directeur
    // zonder bestuurlijke verantwoordelijkheid (Stichting-model met
    // afzonderlijk bestuur) scoort N-1 op de oranje criteria.
    subFamilies: [
      {
        id: "directeur-bestuurder",
        label: "Directeur-bestuurder (RvT-model)",
        norm: { vereist: 5, totaal: 8 },
      },
      {
        id: "directeur-zonder-bestuur",
        label: "Directeur (Stichting-model, zonder bestuurlijke verantwoordelijkheid)",
        norm: { vereist: 5, totaal: 8 },
        scoreShift: { inhoud_programmering: 1, budget: 1 },
      },
    ],
    defaultSubFamilieId: "directeur-bestuurder",
    criteria: [
      {
        id: "inhoud_programmering",
        label: "Inhoud programmering",
        toelichting: "De programmering verwijst naar het samenstellen en plannen van het volledige aanbod van films en aanverwante activiteiten (Educatie, Talent, Industry, Nieuwe Media) die tijdens het festival worden geprogrammeerd, de artistieke en maatschappelijke betekenis en uniciteit ervan, en de randvoorwaarden die daarvoor nodig zijn.",
        keuzes: [
          { niveau: 1, label: "Selectie van meer dan 200 films: overwegend wereldpremières én aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
          { niveau: 2, label: "Selectie van meer dan 200 films (overwegend Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
          { niveau: 3, label: "Selectie van minder dan 200 films (overwegend wereldpremières en/of Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, financiële randvoorwaarden." },
          { niveau: 4, label: "Selectie van minder dan 200 films (minder dan de helft wereldpremières en/of Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
        ],
      },
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        toelichting: "Eindverantwoordelijk voor aantal deelsectoren waarbij elke deelsector uit meerdere programma's bestaat en op meerdere dagen plaatsvindt.",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren: Filmprogramma, Educatie, Talent, Industry en Nieuwe Media." },
          { niveau: 2, label: "4 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 3, label: "3 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 4, label: "1 of 2 deelsectoren: Filmprogramma, Educatie, Talent, Industry, en/of Nieuwe Media." },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen ten aanzien van het resultaat geven aan één en/of twee type werkenden. Tijdens en rondom het festival/festivaldagen neemt het aantal medewerkers waaraan leiding wordt gegeven toe, evenals het aantal zzp-ers/freelancers waaraan aanwijzingen ten aanzien van het eindresultaat wordt gegeven. Bij toepassing Intern en Extern prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 50" },
              { niveau: 2, label: "25 – 49" },
              { niveau: 3, label: "5 – 24" },
              { niveau: 4, label: "1 – 4" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan zzp-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 100" },
              { niveau: 2, label: "50 – 99" },
              { niveau: 3, label: "25 – 49" },
              { niveau: 4, label: "5 – 24" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "4 – 5" },
          { niveau: 4, label: "1 – 3" },
        ],
      },
      {
        id: "budget",
        label: "Budgetverantwoordelijkheid",
        toelichting: "De budgetverantwoordelijkheid op basis van de totale gerealiseerde omzet zoals gerapporteerd in de jaarrekening.",
        keuzes: [
          { niveau: 1, label: "Groot met een jaaromzet van € 5.000.001 - of meer" },
          { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
          { niveau: 3, label: "Klein met een jaaromzet van € 500.001 t/m € 1.000.000" },
          { niveau: 4, label: "Kleiner met een jaaromzet t/m € 500.000" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Het totaal aantal geregistreerde bezoeken fysiek aan films en festivalactiviteiten zoals gerapporteerd in het jaarverslag. Aantallen online bezoeken zijn in deze getallen nog niet meegenomen.",
        keuzes: [
          { niveau: 1, label: "x > 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "x ≤ 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        toelichting: "De uitstraling en invloed van keuzes en handelingen op maatschappelijk en cultureel vlak en hoe deze bijdragen aan representatie en herkenning.",
        keuzes: [
          { niveau: 1, label: "Helder imago, herkenbare identiteit, en uitstraling op internationaal én nationaal cultureel en maatschappelijk gebied en internationale vertegenwoordiging." },
          { niveau: 2, label: "Helder imago, herkenbare identeit en uitstraling op nationaal, regionaal én lokaal cultureel en maatschappelijk gebied en nationale vertegenwoordiging." },
          { niveau: 3, label: "Helder imago, herkenbare identiteit en uitstraling op overwegend provinciale/regionale culturele en maatschappelijke omgeving en regionale vertegenwoordiging." },
          { niveau: 4, label: "Helder imago, herkenbare identeit en uitstraling op overwegend stedelijke culturele en maatschappelijke omgeving en lokale vertegenwoordiging." },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        toelichting: "De noodzakelijke directe afstemming van (beleids-)plannen en/of processen met partijen buiten de eigen organisatie en het noodzakelijke onderhoud van het netwerk (publiek, politiek, media, industry, publieke en private financiers). Dit onderhoud vindt ten minste jaarlijks plaats. Voor de noodzakelijke afstemming is het aantal partijen buiten de eigen organisatie bepalend.",
        keuzes: [
          { niveau: 1, label: "Afstemming met diverse partijen buiten de eigen organisatie (x > 100)." },
          { niveau: 2, label: "Afstemming met diverse partijen buiten de eigen organisatie (75 ≤ x < 100)." },
          { niveau: 3, label: "Afstemming met partijen buiten de eigen organisatie (50 ≤ x < 75)." },
          { niveau: 4, label: "Afstemming met diverse partijen (minder dan 50) buiten de eigen organisatie." },
        ],
      },
    ],
  },

  "Zakelijk Directeur": {
    type: "kwantitatief",
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 51–52.
    // Oranje kolom is Budget: directeur zonder bestuurlijke
    // verantwoordelijkheid scoort N-1 op deze kolom.
    subFamilies: [
      {
        id: "directeur-bestuurder",
        label: "Zakelijk Directeur-bestuurder (RvT-model)",
        norm: { vereist: 4, totaal: 7 },
      },
      {
        id: "directeur-zonder-bestuur",
        label: "Zakelijk Directeur (Stichting-model, zonder bestuurlijke verantwoordelijkheid)",
        norm: { vereist: 4, totaal: 7 },
        scoreShift: { budget: 1 },
      },
    ],
    defaultSubFamilieId: "directeur-bestuurder",
    criteria: [
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        toelichting: "Eindverantwoordelijk voor aantal deelsectoren waarbij elke deelsector uit meerdere programma's bestaat en op meerdere dagen plaatsvindt.",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren: Filmprogramma, Educatie, Talent, Industry en Nieuwe Media." },
          { niveau: 2, label: "4 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 3, label: "3 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 4, label: "1 of 2 deelsectoren: Filmprogramma, Educatie, Talent, Industry, en/of Nieuwe Media." },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen ten aanzien van het resultaat geven aan één en/of twee type werkenden. Tijdens en rondom het festival/festivaldagen neemt het aantal medewerkers waaraan leiding wordt gegeven toe, evenals het aantal zzp-ers/freelancers waaraan aanwijzingen ten aanzien van het eindresultaat wordt gegeven. Bij toepassing Intern en Extern prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 50" },
              { niveau: 2, label: "25 – 49" },
              { niveau: 3, label: "5 – 24" },
              { niveau: 4, label: "1 – 4" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan zzp-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 100" },
              { niveau: 2, label: "50 – 99" },
              { niveau: 3, label: "25 – 49" },
              { niveau: 4, label: "5 – 24" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "4 – 5" },
          { niveau: 4, label: "1 – 3" },
        ],
      },
      {
        id: "budget",
        label: "Budgetverantwoordelijkheid",
        toelichting: "De budgetverantwoordelijkheid op basis van de totale gerealiseerde omzet zoals gerapporteerd in de jaarrekening.",
        keuzes: [
          { niveau: 1, label: "Groot met een jaaromzet van € 5.000.001 - of meer" },
          { niveau: 2, label: "Middelgroot met een jaaromzet van € 1.000.001 t/m € 5.000.000" },
          { niveau: 3, label: "Klein met een jaaromzet van € 500.001 t/m € 1.000.000" },
          { niveau: 4, label: "Kleiner met een jaaromzet t/m € 500.000" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Het totaal aantal geregistreerde bezoeken fysiek aan films en festivalactiviteiten zoals gerapporteerd in het jaarverslag. Aantallen online bezoeken zijn in deze getallen nog niet meegenomen.",
        keuzes: [
          { niveau: 1, label: "x > 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "x ≤ 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        toelichting: "De uitstraling en invloed van keuzes en handelingen op maatschappelijk en cultureel vlak en hoe deze bijdragen aan representatie en herkenning.",
        keuzes: [
          { niveau: 1, label: "Helder imago, herkenbare identiteit, en uitstraling op internationaal én nationaal cultureel en maatschappelijk gebied en internationale vertegenwoordiging." },
          { niveau: 2, label: "Helder imago, herkenbare identeit en uitstraling op nationaal, regionaal én lokaal cultureel en maatschappelijk gebied en nationale vertegenwoordiging." },
          { niveau: 3, label: "Helder imago, herkenbare identiteit en uitstraling op overwegend provinciale/regionale culturele en maatschappelijke omgeving en regionale vertegenwoordiging." },
          { niveau: 4, label: "Helder imago, herkenbare identeit en uitstraling op overwegend stedelijke culturele en maatschappelijke omgeving en lokale vertegenwoordiging." },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        toelichting: "De noodzakelijke directe afstemming van (beleids-)plannen en/of processen met partijen buiten de eigen organisatie en het noodzakelijke onderhoud van het netwerk (publiek, politiek, media, industry, publieke en private financiers). Dit onderhoud vindt ten minste jaarlijks plaats. Voor de noodzakelijke afstemming is het aantal partijen buiten de eigen organisatie bepalend.",
        keuzes: [
          { niveau: 1, label: "Afstemming met diverse partijen buiten de eigen organisatie (x > 100)." },
          { niveau: 2, label: "Afstemming met diverse partijen buiten de eigen organisatie (75 ≤ x < 100)." },
          { niveau: 3, label: "Afstemming met partijen buiten de eigen organisatie (50 ≤ x < 75)." },
          { niveau: 4, label: "Afstemming met diverse partijen (minder dan 50) buiten de eigen organisatie." },
        ],
      },
    ],
  },

  "Artistiek Directeur": {
    type: "kwantitatief",
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    // Bron: Functiehandboek Filmfestivals 2.0 (dec 2025), p. 53–54.
    // Oranje kolom is Inhoud programmering: directeur zonder
    // bestuurlijke verantwoordelijkheid scoort N-1 op deze kolom.
    subFamilies: [
      {
        id: "directeur-bestuurder",
        label: "Artistiek Directeur-bestuurder (RvT-model)",
        norm: { vereist: 4, totaal: 7 },
      },
      {
        id: "directeur-zonder-bestuur",
        label: "Artistiek Directeur (Stichting-model, zonder bestuurlijke verantwoordelijkheid)",
        norm: { vereist: 4, totaal: 7 },
        scoreShift: { inhoud_programmering: 1 },
      },
    ],
    defaultSubFamilieId: "directeur-bestuurder",
    criteria: [
      {
        id: "inhoud_programmering",
        label: "Inhoud programmering",
        toelichting: "De programmering verwijst naar het samenstellen en plannen van het volledige aanbod van films en aanverwante activiteiten (Educatie, Talent, Industry, Nieuwe Media) die tijdens het festival worden geprogrammeerd, de artistieke en maatschappelijke betekenis en uniciteit ervan, en de randvoorwaarden die daarvoor nodig zijn.",
        keuzes: [
          { niveau: 1, label: "Selectie van meer dan 200 films (overwegend wereldpremières én Europese premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
          { niveau: 2, label: "Selectie van meer dan 200 films (overwegend Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
          { niveau: 3, label: "Selectie van minder dan 200 films (overwegend wereldpremières en/of Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, financiële randvoorwaarden." },
          { niveau: 4, label: "Selectie van minder dan 200 films (minder dan de helft wereldpremières en/of Europese premières en/of nationale premières) en aanverwante activiteiten met bijbehorende inhoudelijke, maatschappelijke, én financiële randvoorwaarden." },
        ],
      },
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        toelichting: "Eindverantwoordelijk voor aantal deelsectoren waarbij elke deelsector uit meerdere programma's bestaat en op meerdere dagen plaatsvindt.",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren: Filmprogramma, Educatie, Talent, Industry en Nieuwe Media." },
          { niveau: 2, label: "4 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 3, label: "3 deelsectoren: Filmprogramma, Educatie, Talent, Industry en/of Nieuwe Media." },
          { niveau: 4, label: "1 of 2 deelsectoren: Filmprogramma, Educatie, Talent, Industry, en/of Nieuwe Media." },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Leiding en/of aanwijzingen ten aanzien van het resultaat geven aan één en/of twee type werkenden. Tijdens en rondom het festival/festivaldagen neemt het aantal medewerkers waaraan leiding wordt gegeven toe, evenals het aantal zzp-ers/freelancers waaraan aanwijzingen ten aanzien van het eindresultaat wordt gegeven. Bij toepassing Intern en Extern prevaleert de totaalscore van het zwaarste scorende niveau.",
        subCriteria: [
          {
            id: "intern",
            label: "Intern: hiërarchisch leidinggeven aan werknemers (direct én indirect)",
            keuzes: [
              { niveau: 1, label: "x ≥ 50" },
              { niveau: 2, label: "25 – 49" },
              { niveau: 3, label: "5 – 24" },
              { niveau: 4, label: "1 – 4" },
            ],
          },
          {
            id: "extern",
            label: "Extern: aanwijzingen aan zzp-ers (alleen direct)",
            keuzes: [
              { niveau: 1, label: "x ≥ 100" },
              { niveau: 2, label: "50 – 99" },
              { niveau: 3, label: "25 – 49" },
              { niveau: 4, label: "5 – 24" },
            ],
          },
        ],
        aggregatie: { strategie: "zwaarste" },
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival wordt verricht.",
        keuzes: [
          { niveau: 1, label: "x ≥ 11" },
          { niveau: 2, label: "6 – 10" },
          { niveau: 3, label: "4 – 5" },
          { niveau: 4, label: "1 – 3" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Het totaal aantal geregistreerde bezoeken fysiek aan films en festivalactiviteiten zoals gerapporteerd in het jaarverslag. Aantallen online bezoeken zijn in deze getallen nog niet meegenomen.",
        keuzes: [
          { niveau: 1, label: "x > 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "x ≤ 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        toelichting: "De uitstraling en invloed van keuzes en handelingen op maatschappelijk en cultureel vlak en hoe deze bijdragen aan representatie en herkenning.",
        keuzes: [
          { niveau: 1, label: "Helder imago, herkenbare identiteit, en uitstraling op internationaal én nationaal cultureel en maatschappelijk gebied en internationale vertegenwoordiging." },
          { niveau: 2, label: "Helder imago, herkenbare identeit en uitstraling op nationaal, regionaal én lokaal cultureel en maatschappelijk gebied en nationale vertegenwoordiging." },
          { niveau: 3, label: "Helder imago, herkenbare identiteit en uitstraling op overwegend provinciale/regionale culturele en maatschappelijke omgeving en regionale vertegenwoordiging." },
          { niveau: 4, label: "Helder imago, herkenbare identeit en uitstraling op overwegend stedelijke culturele en maatschappelijke omgeving en lokale vertegenwoordiging." },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        toelichting: "De noodzakelijke directe afstemming van (beleids-)plannen en/of processen met partijen buiten de eigen organisatie en het noodzakelijke onderhoud van het netwerk (publiek, politiek, media, industry, publieke en private financiers). Dit onderhoud vindt ten minste jaarlijks plaats. Voor de noodzakelijke afstemming is het aantal partijen buiten de eigen organisatie bepalend.",
        keuzes: [
          { niveau: 1, label: "Afstemming met diverse partijen buiten de eigen organisatie (x > 100)." },
          { niveau: 2, label: "Afstemming met diverse partijen buiten de eigen organisatie (75 ≤ x < 100)." },
          { niveau: 3, label: "Afstemming met partijen buiten de eigen organisatie (50 ≤ x < 75)." },
          { niveau: 4, label: "Afstemming met diverse partijen (minder dan 50) buiten de eigen organisatie." },
        ],
      },
    ],
  },
};

// ============================================================
// SCORE-ALGORITME
// ============================================================

// Resolve een criterium-antwoord naar één niveau (voor kwantitatief) of
// variant-letter (kwalitatief). Voor criteria met subCriteria past de
// aggregatie-strategie toe; voor enkelvoudige criteria past het de oude vorm
// 1-op-1 door.
function resolveCriteriumAntwoord(criterium, antwoord) {
  if (!antwoord) return null;
  if (!criterium.subCriteria) return antwoord;

  const sub = antwoord.sub || {};
  const subEntries = Object.values(sub).filter(v => v && Number.isFinite(v.niveau));
  if (subEntries.length === 0) return null;

  const strat = criterium.aggregatie?.strategie || "zwaarste";
  if (strat === "zwaarste") {
    return { niveau: Math.min(...subEntries.map(e => e.niveau)) };
  }
  if (strat === "prevaleert") {
    const winnaarId = criterium.aggregatie?.winnaar;
    const w = winnaarId ? sub[winnaarId] : null;
    if (w && Number.isFinite(w.niveau)) return { niveau: w.niveau };
    return { niveau: Math.min(...subEntries.map(e => e.niveau)) };
  }
  return null;
}

// Resolve de norm + uitgesloten kolommen + score-shift voor de huidige
// sub-familie keuze. Families zonder subFamilies vallen terug op de
// familie-norm zelf.
//
// scoreShift wordt gebruikt voor de directeur-zonder-bestuurlijke-
// verantwoordelijkheid-regel ("N-1 op oranje criteria", p.49/51/53):
// een sub-familie kan per criterium een vast aantal niveaus optellen bij
// het gescoorde niveau (hoger niveau = lichter werk in deze conventie).
function resolveSubFamilie(fam, subFamilieId) {
  if (!fam.subFamilies?.length) {
    return { norm: fam.norm, uitgesloten: new Set(), scoreShift: null };
  }
  const sf =
    fam.subFamilies.find(s => s.id === subFamilieId) ||
    fam.subFamilies.find(s => s.id === fam.defaultSubFamilieId) ||
    fam.subFamilies[0];
  return {
    norm: sf.norm,
    uitgesloten: new Set(sf.uitgesloten || []),
    scoreShift: sf.scoreShift || null,
  };
}

/**
 * Bepaal de variant (A/B/C of A/B) op basis van de gegeven antwoorden.
 * @param {Object} antwoorden    - { [criteriumId]: keuzeObject | { sub: { [subId]: keuzeObject } } | null }
 * @param {string} familieKey    - sleutel in FAMILIE_CRITERIA
 * @param {string=} subFamilieId - sleutel binnen fam.subFamilies (optioneel, valt terug op defaultSubFamilieId)
 * @returns {{ variant: string|null, gescoord: number, vereist: number, totaal: number, uitleg: string }}
 */
export function bepaalVariant(antwoorden, familieKey, subFamilieId) {
  const fam = FAMILIE_CRITERIA[familieKey];
  if (!fam) return null;

  const { norm, uitgesloten, scoreShift } = resolveSubFamilie(fam, subFamilieId);

  // Effectieve criteria = familie-criteria minus uitgesloten kolommen voor
  // deze sub-familie. Antwoorden van uitgesloten criteria tellen niet mee.
  // scoreShift (optioneel per criterium) verhoogt het gescoorde niveau,
  // een hoger niveau-nummer betekent lichter werk, dus shift +1 maakt het
  // criterium effectief 1 stap lichter. Buiten range valt het criterium
  // uit de cumulatieve telling en kan de fallbackVariant gelden.
  const effectieveCriteria = fam.criteria.filter(c => !uitgesloten.has(c.id));
  const beantwoord = effectieveCriteria
    .map(c => {
      const ant = resolveCriteriumAntwoord(c, antwoorden?.[c.id]);
      if (!ant) return null;
      const shift = scoreShift?.[c.id];
      if (shift && Number.isFinite(ant.niveau)) {
        return { ...ant, niveau: ant.niveau + shift };
      }
      return ant;
    })
    .filter(Boolean);
  const totaalBeantwoord = beantwoord.length;

  if (fam.type === "kwantitatief") {
    // Cumulatief van zwaarste niveau (1) naar lichtste (max): eerste niveau N
    // waarop ≥ vereist criteria gescoord zijn op niveau ≤ N bepaalt de variant.
    // Range wordt afgeleid uit variantBijNiveau (3 voor Hoofden, 4 voor Directie).
    const niveaus = beantwoord.map(a => a.niveau).filter(n => Number.isFinite(n));
    const maxNiveau = Math.max(...Object.keys(fam.variantBijNiveau).map(Number));
    for (let n = 1; n <= maxNiveau; n++) {
      const aantalOpOfBoven = niveaus.filter(x => x <= n).length;
      if (aantalOpOfBoven >= norm.vereist) {
        return {
          variant: fam.variantBijNiveau[n],
          gescoord: aantalOpOfBoven,
          vereist: norm.vereist,
          totaal: norm.totaal,
          beantwoord: totaalBeantwoord,
          uitleg: `${aantalOpOfBoven} van ${norm.totaal} criteria voldoen op niveau ${n} of zwaarder → variant ${fam.variantBijNiveau[n]}.`,
        };
      }
    }
    // Norm niet gehaald: families met expliciete fallbackVariant (Directie:
    // lege A-rij in handboek) vallen daarop terug: maar pas zodra ALLE
    // effectieve criteria beantwoord zijn (anders verschijnt A al bij 0
    // antwoorden). Andere families (Hoofden) geven null tot er een variant
    // matcht.
    const alleBeantwoord = totaalBeantwoord >= effectieveCriteria.length;
    if (fam.fallbackVariant && alleBeantwoord) {
      return {
        variant: fam.fallbackVariant,
        gescoord: niveaus.filter(x => x <= maxNiveau).length,
        vereist: norm.vereist,
        totaal: norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `Norm (${norm.vereist} van ${norm.totaal}) niet gehaald → variant ${fam.fallbackVariant} (laagste schaal).`,
      };
    }
    return {
      variant: null,
      gescoord: niveaus.filter(x => x <= maxNiveau).length,
      vereist: norm.vereist,
      totaal: norm.totaal,
      beantwoord: totaalBeantwoord,
      uitleg: `Norm niet gehaald (${norm.vereist} van ${norm.totaal} criteria vereist). Beantwoord meer vragen.`,
    };
  }

  if (fam.type === "kwalitatief") {
    const aCount = beantwoord.filter(x => x.variant === "A").length;
    const bCount = beantwoord.filter(x => x.variant === "B").length;
    // B-variant is zwaarder; hanteer B prevaleert bij norm-overschrijding
    if (bCount >= norm.vereist) {
      return {
        variant: "B",
        gescoord: bCount,
        vereist: norm.vereist,
        totaal: norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `${bCount} van ${norm.totaal} criteria op niveau B → variant B.`,
      };
    }
    if (aCount >= norm.vereist) {
      return {
        variant: "A",
        gescoord: aCount,
        vereist: norm.vereist,
        totaal: norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `${aCount} van ${norm.totaal} criteria op niveau A → variant A.`,
      };
    }
    // B-norm niet gehaald: val terug op de laagste variant A, want er bestaat geen
    // "geen duidelijke variant". De inschaling gaat van het hoogste naar het
    // laagste niveau (Functiehandboek pag. 55-56). Pas zodra alle criteria
    // beantwoord zijn, anders verschijnt A al bij onvolledige invoer.
    const alleBeantwoord = totaalBeantwoord >= effectieveCriteria.length;
    if (alleBeantwoord) {
      return {
        variant: "A",
        gescoord: aCount,
        vereist: norm.vereist,
        totaal: norm.totaal,
        beantwoord: totaalBeantwoord,
        aCount,
        bCount,
        uitleg: `Je voldoet niet aan ${norm.vereist} van de ${norm.totaal} criteria op variant B. Hierdoor is variant A van toepassing. Zie pagina 55 van het Functiehandboek Referentiefuncties Filmfestivals.`,
      };
    }
    return {
      variant: null,
      gescoord: Math.max(aCount, bCount),
      vereist: norm.vereist,
      totaal: norm.totaal,
      beantwoord: totaalBeantwoord,
      aCount,
      bCount,
      uitleg: `Beantwoord alle vragen om je variant te bepalen.`,
    };
  }

  return null;
}

export function hasCriteria(familieKey) {
  return Boolean(FAMILIE_CRITERIA[familieKey]);
}
